import { urls } from 'Reducers/user';
import Auth from '../Auth';
import { getFilledCollection } from 'Reducers/componentHelpers';
import { paramsToQueryString } from 'Helpers/url';
import { getSelectedLangCode } from './config';
import { getType } from 'Reducers/helpers/typeModifier';
import { isEmpty } from 'Helpers/objects';
import { isCocoFofoUser } from 'Helpers/item';

const isFetching = (state, url) => {
    const lang = getSelectedLangCode(state);

    if (lang) {
        url = `${url}?${paramsToQueryString({ lang })}`;
    }
    return Boolean(state.users.isFetching[getType('USERS', url)]);
};

const userIsLogged = state => {
    if (!state) {
        return false;
    }

    return Auth.isLoggedIn() && !isEmpty(Auth.getUser());
};

const getUserDataSelector = state => {
    if (!(state && state.users && state.users.collections)) {
        return {};
    }
    let url = urls.getUserData;
    const lang = getSelectedLangCode(state);

    if (lang) {
        url = `${url}?${paramsToQueryString({ lang })}`;
    }
    return state.users.collections[getType('USERS', url)];
};

const getUserData = state => {
    const user = getUserDataSelector(state);

    if (user?.id) {
        return user;
    }
    return Auth.getUser() || {};
};

const getLoggedUser = state => {
    if (!state || !userIsLogged(state)) {
        return {};
    }

    const loggedInUser = (state.user && state.user.userData && state.user.userData.user) || getUserDataSelector(state);
    const userCookie = Auth.getUser() || {};

    if (isEmpty(loggedInUser)) {
        return userCookie;
    }

    // nameProvided and avatarId is for
    const { name_provided, avatar_id, images, nameProvided, avatarId } = loggedInUser;
    const image = images && images.length && images[0].url ? images[0].url : '';
    const { subscriptionInfo: { endDate, startDate, type: type_user } = {}, subscription_info: { end_date, start_date, type } = {}} = loggedInUser;

    if (loggedInUser.subscriptionInfo) {
        loggedInUser.subscription_info = {
            end_date: endDate || end_date,
            start_date: startDate || start_date,
            type: type_user || type
        };
        delete loggedInUser.subscriptionInfo;
    }

    return {
        ...loggedInUser,
        ...userCookie,
        name_provided: name_provided || nameProvided,
        avatar_id: avatar_id || avatarId,
        image
    };
};

const getUserImagesDataSelector = (state, type) => {
    if (!state) {
        return [];
    }

    const user = getLoggedUser(state);

    return state.users.collections[getType('USERS', urls.getUserImagesData(user.id, type))];
};

const isFetchingUserData = state => isFetching(state, urls.getUserData);

const isFetchingUpdateExistingUser = state => {
    const user = getLoggedUser(state);

    return isFetching(state, urls.updateUser(user.id));
};

const isFetchingUnLinkAccount = state => {
    const user = getLoggedUser(state);

    return isFetching(state, urls.unLinkAccount(user.id));
};

const isFetchingLinkAccount = state => isFetching(state, urls.linkAccount);

const isFetchingCreateChallenge = state => isFetching(state, urls.createChallenge);

const isFetchingValidateChallenge = state => isFetching(state, urls.validateChallenge);

const getFollowersSelector = state => {
    if (!state) {
        return [];
    }

    const user = getLoggedUser(state);

    return getFilledCollection('USERS', state.users, urls.getFollowers(user.id));
};

const isFetchingFollowers = state => {
    const user = getLoggedUser(state);

    return isFetching(state, urls.getFollowers(user.id));
};

const getFollowingSelector = state => {
    if (!state) {
        return [];
    }

    const user = getLoggedUser(state);

    return getFilledCollection('USERS', state.users, urls.getFollowing(user.id));
};

const isFetchingFollowing = state => {
    const user = getLoggedUser(state);

    return isFetching(state, urls.getFollowing(user.id));
};

const isFetchingUpdatingPhotos = state => {
    const user = getLoggedUser(state);

    return isFetching(state, urls.updateCoverAndBusinessPhotos(user.id));
};

const isError = (state, url) => {
    const lang = getSelectedLangCode(state);

    if (lang) {
        url = `${url}?${paramsToQueryString({ lang })}`;
    }

    return Boolean(state.users.isError[url]);
};

const isUserFetchingError = state => {
    if (!state) {
        return {};
    }
    let url = urls.getUserData;
    const lang = state.api && state.api.queryParam && state.api.queryParam.enableMultiLang && state.api.queryParam.lang;

    if (lang) {
        url = `${url}?${paramsToQueryString({ lang })}`;
    }
    return Boolean(state.users.isError[getType('USERS', url)]);
};

const isGetExistingUserError = (state, id) => {
    if (isError(state, urls.getExistingUser(id))) {
        return true;
    }
    return false;
};

const favouritesIdsSelector = state => state.user && state.user.favouritesItems;

const favouritesIdsCountSelector = state => {
    const userFavIds = favouritesIdsSelector(state) || [];

    return userFavIds.filter(id => isCocoFofoUser(id?.dealerType)).length;
};

const favouriteItemSelector = (state, itemId) => {
    itemId = parseInt(itemId, 10);
    return state.user?.favouritesItems?.filter(item => item?.adId === itemId)[0];
};

const favouriteItemsSelector = (state, itemId) => {
    itemId = parseInt(itemId, 10);
    return !!state.user?.favouritesItems?.map(item => item?.adId).includes(itemId);
};

// Legion
const userAuthenticateData = state => {
    if (!state || !state.user || !state.user.userData) {
        return {};
    }

    return state.user.userData;
};

export {
    getUserDataSelector,
    isFetchingUserData,
    getLoggedUser,
    getFollowersSelector,
    isFetchingFollowers,
    getFollowingSelector,
    isFetchingFollowing,
    userIsLogged,
    isFetching,
    isError,
    isGetExistingUserError,
    favouriteItemsSelector,
    favouritesIdsSelector,
    isFetchingUpdateExistingUser,
    isFetchingUnLinkAccount,
    isFetchingLinkAccount,
    isFetchingCreateChallenge,
    isFetchingValidateChallenge,
    isUserFetchingError,
    userAuthenticateData,
    favouriteItemSelector,
    favouritesIdsCountSelector,
    getUserData,
    getUserImagesDataSelector,
    isFetchingUpdatingPhotos
};
